/**
 * Admin Dashboard Page
 *
 * Analytics overview for super admin:
 * - Total users, revenue, orders
 * - Recent registrations and active users
 * - Service usage breakdown
 * - Revenue by package
 * - Recent orders table
 *
 * CRITICAL CONSTRAINTS:
 * - TypeScript strict mode (NO 'any' types)
 * - Real-time Firestore queries
 * - NO localStorage
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getCountFromServer,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/firebaseConfig';
import { Card, LoadingSkeleton } from '@/components/ui';
import { AdminHeader } from '@/components/admin/AdminHeader';
import { ServiceType, OrderStatus, TransactionStatus } from '@/shared/types';
import { formatDate, getServiceName } from '@/utils/formatting';

interface DashboardStats {
  totalUsers: number;
  totalRevenue: number;
  totalOrders: number;
  recentRegistrations: number;
}

interface ServiceUsageItem {
  serviceType: ServiceType;
  count: number;
}

interface RevenueItem {
  packageName: string;
  revenue: number;
  count: number;
}

interface RecentOrderItem {
  orderId: string;
  userEmail: string;
  serviceType: ServiceType;
  status: OrderStatus;
  createdAt: Timestamp;
}

export function AdminDashboard(): React.JSX.Element {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalRevenue: 0,
    totalOrders: 0,
    recentRegistrations: 0,
  });
  const [serviceUsage, setServiceUsage] = useState<ServiceUsageItem[]>([]);
  const [revenueByPackage, setRevenueByPackage] = useState<RevenueItem[]>([]);
  const [recentOrders, setRecentOrders] = useState<RecentOrderItem[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * Load analytics data
   */
  useEffect(() => {
    const loadAnalytics = async (): Promise<void> => {
      try {
        setLoading(true);

        // Get total users count
        const usersCountSnapshot = await getCountFromServer(collection(db, 'users'));
        const totalUsers = usersCountSnapshot.data().count;

        // Get recent registrations (last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recentUsersQuery = query(
          collection(db, 'users'),
          where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo))
        );
        const recentUsersSnapshot = await getCountFromServer(recentUsersQuery);
        const recentRegistrations = recentUsersSnapshot.data().count;

        // Get total orders count
        const ordersCountSnapshot = await getCountFromServer(collection(db, 'orders'));
        const totalOrders = ordersCountSnapshot.data().count;

        // Get total revenue from completed transactions
        const completedTransactionsQuery = query(
          collection(db, 'transactions'),
          where('status', '==', TransactionStatus.COMPLETED)
        );
        const transactionsSnapshot = await getDocs(completedTransactionsQuery);
        let totalRevenue = 0;
        const packageRevenue: Record<string, { revenue: number; count: number; name: string }> = {};

        transactionsSnapshot.forEach((doc) => {
          const data = doc.data();
          totalRevenue += data.amountCAD || 0;

          // Aggregate by package
          const packageId = data.packageId || 'unknown';
          if (!packageRevenue[packageId]) {
            packageRevenue[packageId] = {
              revenue: 0,
              count: 0,
              name: data.packageName || packageId,
            };
          }
          packageRevenue[packageId].revenue += data.amountCAD || 0;
          packageRevenue[packageId].count += 1;
        });

        // Convert to array and sort by revenue
        const revenueArray = Object.values(packageRevenue).sort(
          (a, b) => b.revenue - a.revenue
        );

        // Get service usage breakdown
        const ordersSnapshot = await getDocs(collection(db, 'orders'));
        const serviceUsageMap: Record<ServiceType, number> = {
          [ServiceType.DAY_TO_DUSK]: 0,
          [ServiceType.ITEM_REMOVAL]: 0,
          [ServiceType.STAGING]: 0,
          [ServiceType.DESCRIPTION]: 0,
          [ServiceType.DESTAGING]: 0,
          [ServiceType.COLLAGE]: 0,
          [ServiceType.WATERMARK]: 0,
          [ServiceType.ENHANCEMENT]: 0,
        };

        ordersSnapshot.forEach((doc) => {
          const data = doc.data();
          const serviceType = data.serviceType as ServiceType;
          if (serviceUsageMap[serviceType] !== undefined) {
            serviceUsageMap[serviceType] += 1;
          }
        });

        const serviceUsageArray = Object.entries(serviceUsageMap).map(([serviceType, count]) => ({
          serviceType: serviceType as ServiceType,
          count,
        }));

        // Get recent orders (last 10)
        const recentOrdersQuery = query(
          collection(db, 'orders'),
          orderBy('createdAt', 'desc'),
          limit(10)
        );
        const recentOrdersSnapshot = await getDocs(recentOrdersQuery);
        const recentOrdersArray: RecentOrderItem[] = [];

        for (const doc of recentOrdersSnapshot.docs) {
          const orderData = doc.data();

          // Fetch user email
          const userDoc = await getDocs(
            query(collection(db, 'users'), where('uid', '==', orderData.userId), limit(1))
          );
          const userEmail = userDoc.empty
            ? 'Unknown'
            : (userDoc.docs[0].data().email as string);

          recentOrdersArray.push({
            orderId: doc.id,
            userEmail,
            serviceType: orderData.serviceType as ServiceType,
            status: orderData.status as OrderStatus,
            createdAt: orderData.createdAt as Timestamp,
          });
        }

        // Update state
        setStats({
          totalUsers,
          totalRevenue,
          totalOrders,
          recentRegistrations,
        });
        setServiceUsage(serviceUsageArray);
        setRevenueByPackage(
          revenueArray.map((item) => ({
            packageName: item.name,
            revenue: item.revenue,
            count: item.count,
          }))
        );
        setRecentOrders(recentOrdersArray);
        setLoading(false);
      } catch (error) {
        console.error('Error loading analytics:', error);
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  /**
   * Get status badge color
   */
  const getStatusColor = (status: OrderStatus): string => {
    switch (status) {
      case OrderStatus.COMPLETED:
        return 'bg-green-100 text-green-800';
      case OrderStatus.PROCESSING:
        return 'bg-blue-100 text-blue-800';
      case OrderStatus.FAILED:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <AdminHeader />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="mt-1 text-sm text-gray-600">
            Platform analytics and insights
          </p>
        </div>

        {/* Stats Cards */}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Total Users</h3>
                <span className="text-3xl">👥</span>
              </div>
              {loading ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-24" />
              ) : (
                <p className="text-4xl font-bold text-blue-700">{stats.totalUsers}</p>
              )}
              {!loading && (
                <p className="mt-2 text-sm text-gray-600">
                  +{stats.recentRegistrations} last 30 days
                </p>
              )}
            </div>
          </Card>

          {/* Total Revenue */}
          <Card className="bg-gradient-to-br from-green-50 to-green-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Total Revenue</h3>
                <span className="text-3xl">💰</span>
              </div>
              {loading ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-32" />
              ) : (
                <p className="text-4xl font-bold text-green-700">
                  ${stats.totalRevenue.toFixed(2)}
                </p>
              )}
              <p className="mt-2 text-sm text-gray-600">CAD</p>
            </div>
          </Card>

          {/* Total Orders */}
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Total Orders</h3>
                <span className="text-3xl">📦</span>
              </div>
              {loading ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-24" />
              ) : (
                <p className="text-4xl font-bold text-purple-700">{stats.totalOrders}</p>
              )}
            </div>
          </Card>

          {/* Avg Order Value */}
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Avg Order Value</h3>
                <span className="text-3xl">📊</span>
              </div>
              {loading ? (
                <LoadingSkeleton variant="text" height="h-8" width="w-24" />
              ) : (
                <p className="text-4xl font-bold text-yellow-700">
                  ${stats.totalOrders > 0 ? (stats.totalRevenue / stats.totalOrders).toFixed(2) : '0.00'}
                </p>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Service Usage */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Usage</h3>
              {loading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <LoadingSkeleton key={i} variant="text" width="w-full" height="h-8" />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {serviceUsage.map((item) => {
                    const percentage =
                      stats.totalOrders > 0 ? (item.count / stats.totalOrders) * 100 : 0;
                    return (
                      <div key={item.serviceType}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="font-medium">{getServiceName(item.serviceType)}</span>
                          <span className="text-gray-600">
                            {item.count} ({percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>

          {/* Revenue by Package */}
          <Card>
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue by Package</h3>
              {loading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <LoadingSkeleton key={i} variant="text" width="w-full" height="h-8" />
                  ))}
                </div>
              ) : revenueByPackage.length === 0 ? (
                <p className="text-sm text-gray-500">No revenue data available</p>
              ) : (
                <div className="space-y-3">
                  {revenueByPackage.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium">{item.packageName}</p>
                        <p className="text-xs text-gray-500">{item.count} purchases</p>
                      </div>
                      <p className="text-lg font-bold text-green-600">
                        ${item.revenue.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card>
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h3>
            {loading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <LoadingSkeleton key={i} variant="text" width="w-full" height="h-12" />
                ))}
              </div>
            ) : recentOrders.length === 0 ? (
              <p className="text-sm text-gray-500">No orders yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Order ID
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Service
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Created
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {recentOrders.map((order) => (
                      <tr key={order.orderId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">
                          {order.orderId.substring(0, 8)}...
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{order.userEmail}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {getServiceName(order.serviceType)}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                              order.status
                            )}`}
                          >
                            {order.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatDate(order.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      </main>
    </div>
  );
}
